import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { usePayroll, useSettings } from '../../../services/httpServices/queries';
import { hrService, type HrEmployee } from '../../../services/httpServices/hrService';
import { downloadTextFile, toCsv } from '../../../utils/csv';
import { formatDateLabel } from '../../../utils/dateFormat';
import { MonthPicker } from '../../../components/ui/DatePicker';
import { isDocumentSignatureEnabled } from '../../../utils/signatureSettings';
import {
  X,
  Printer,
  Download,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Landmark,
  FileSpreadsheet,
} from 'lucide-react';

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const PAYROLL_STATUSES = ['DRAFT', 'PROCESSED', 'FINALIZED', 'PAID'];

const currentMonth = () => new Date().toISOString().slice(0, 7);

/** "2026-07" -> "July 2026". */
function periodLabel(period?: string): string {
  if (!period) return '—';
  const [year, month] = period.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return period;
  return `${MONTHS_LONG[month - 1]} ${year}`;
}

/** Inclusive first/last day of a `YYYY-MM` period, used when no record is loaded. */
function periodBounds(period: string): { start: string; end: string } {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) return { start: '', end: '' };
  const lastDay = new Date(year, month, 0).getDate();
  return { start: `${period}-01`, end: `${period}-${String(lastDay).padStart(2, '0')}` };
}

function employeeName(employee?: HrEmployee | null): string {
  return (
    employee?.name ||
    [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') ||
    'Employee'
  );
}

const money = (value: number) =>
  `৳${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface BankRow {
  id: string;
  code: string;
  name: string;
  designation: string;
  bank: string;
  account: string;
  amount: number;
  status: string;
  hasBank: boolean;
}

interface BankGroup {
  bank: string;
  rows: BankRow[];
  subtotal: number;
}

/**
 * Self-contained styles for the bank salary sheet, shared by the on-screen
 * preview and the print window so both match.
 */
const SHEET_CSS = `
.bs-sheet { color:#111827; font-family:Arial,Helvetica,sans-serif; font-size:12px; background:#ffffff; padding:16px; }
.bs-sheet * { box-sizing:border-box; }
.bs-head { display:flex; align-items:center; gap:12px; border-bottom:3px solid #0f766e; padding-bottom:10px; }
.bs-logo { max-width:64px; max-height:64px; object-fit:contain; display:block; }
.bs-name-bn { font-size:15px; font-weight:700; color:#334155; }
.bs-name-en { font-size:19px; font-weight:800; color:#0f766e; line-height:1.15; }
.bs-addr { font-size:10px; color:#6b7280; margin-top:2px; }
.bs-contact { font-size:10px; color:#6b7280; margin-top:1px; }
.bs-title { text-align:center; font-size:15px; font-weight:700; letter-spacing:2px; text-transform:uppercase; margin-top:12px; }
.bs-subtitle { text-align:center; font-size:12px; font-weight:600; color:#374151; margin-top:3px; }
.bs-meta { display:flex; justify-content:center; gap:18px; flex-wrap:wrap; font-size:10px; color:#374151; margin-top:6px; }
.bs-section { margin-top:12px; }
.bs-section-title { display:flex; align-items:baseline; justify-content:space-between; gap:8px; font-size:12px; font-weight:700; background:#e5e7eb; padding:5px 8px; border-left:3px solid #0f766e; page-break-after:avoid; }
.bs-section-sub { font-weight:400; font-size:11px; color:#374151; }
.bs-table { width:100%; border-collapse:collapse; margin-top:6px; font-size:11px; }
.bs-table th, .bs-table td { border:1px solid #d1d5db; padding:4px 6px; text-align:left; vertical-align:middle; }
.bs-table thead { display:table-header-group; }
.bs-table tr { page-break-inside:avoid; }
.bs-table th { background:#f9fafb; font-size:9px; text-transform:uppercase; letter-spacing:.4px; text-align:center; }
.bs-serial { width:30px; text-align:center; font-weight:700; }
.bs-code { font-size:10px; color:#6b7280; }
.bs-name { font-weight:600; }
.bs-num { text-align:right; white-space:nowrap; font-variant-numeric:tabular-nums; }
.bs-subtotal td { font-weight:700; background:#f1f5f9; }
.bs-empty { padding:28px; text-align:center; color:#6b7280; }
.bs-grand { margin-top:12px; display:flex; justify-content:flex-end; }
.bs-grand .box { border:2px solid #0f766e; border-radius:6px; padding:8px 16px; font-size:13px; font-weight:700; color:#0f766e; }
.bs-signatures { margin-top:38px; display:flex; align-items:flex-start; justify-content:space-between; gap:40px; }
.bs-signatures .bs-sig-block { flex:1; text-align:center; font-size:10px; color:#475569; }
.bs-sig-graphic { position:relative; height:34px; }
.bs-sig-img { position:absolute; bottom:0; left:50%; transform:translateX(-50%); height:34px; max-width:120px; object-fit:contain; }
.bs-sig-seal { position:absolute; bottom:0; right:4px; height:40px; width:40px; object-fit:contain; opacity:.85; }
.bs-sig-rule { border-top:1px solid #94a3b8; padding-top:5px; }
.bs-sig-name { font-size:9px; color:#6b7280; margin-top:1px; }
.bs-footer { margin-top:16px; padding-top:6px; border-top:1px solid #d1d5db; font-size:10px; color:#6b7280; display:flex; justify-content:space-between; gap:8px; }
`;

interface BankSalarySheetModalProps {
  onClose: () => void;
}

/**
 * Monthly bank salary statement: one row per employee with bank name, account
 * number and net pay, grouped bank-wise with subtotals so the sheet can be
 * handed to the bank for salary disbursement. Supports print, PDF download
 * and a CSV export for bulk-upload portals.
 */
export function BankSalarySheetModal({ onClose }: BankSalarySheetModalProps) {
  const [payPeriod, setPayPeriod] = useState(currentMonth());
  const [status, setStatus] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Close on Escape for a polished, keyboard-friendly experience.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const { data: payrollData, isLoading, isError } = usePayroll({
    payPeriod,
    status: status || undefined,
    page: 1,
    limit: 1000,
  });
  const { data: settingsData } = useSettings();

  const settings = useMemo(() => settingsData?.data || [], [settingsData]);
  const settingValue = (key: string) =>
    settings.find((setting) => setting.key === key)?.value?.trim() || '';

  const hospitalNameEn = settingValue('hospital_name_en') || settingValue('hospital_name') || 'Hospital';
  const hospitalNameBn = settingValue('hospital_name_bn');
  const hospitalLogo = settingValue('hospital_logo');
  const address = settingValue('hospital_address');
  const hotline = settingValue('hospital_hotline');
  const website = settingValue('hospital_website');
  const email = settingValue('hospital_email');
  // Shared with invoices; added only when allowed in General Settings → Documents.
  const signatureImage = settingValue('hospital_signature');
  const sealImage = settingValue('hospital_seal');
  const signatoryName = settingValue('hospital_signatory_name');
  const showSignature =
    isDocumentSignatureEnabled(settingValue, 'salary_sheet') &&
    Boolean(signatureImage || sealImage || signatoryName);

  const rows = useMemo<BankRow[]>(() => {
    return (payrollData?.data || [])
      .map((record: any) => {
        const employee: HrEmployee | null = record.employee || null;
        const bank = (employee?.bankName || '').trim();
        const account = (employee?.bankAccountNumber || '').trim();
        return {
          id: record.id,
          code: employee?.employeeCode || '—',
          name: employeeName(employee),
          designation: employee?.designation?.name || '—',
          bank: bank || 'No bank details',
          account: account || '—',
          amount: Number(record.netSalary ?? record.netPay ?? 0),
          status: record.status,
          hasBank: Boolean(bank || account),
        };
      })
      .sort((a, b) => a.bank.localeCompare(b.bank) || a.name.localeCompare(b.name));
  }, [payrollData]);

  const visibleRows = rows;

  const groups = useMemo<BankGroup[]>(() => {
    const map = new Map<string, BankRow[]>();
    for (const row of visibleRows) {
      const list = map.get(row.bank);
      if (list) list.push(row);
      else map.set(row.bank, [row]);
    }
    return Array.from(map, ([bank, list]) => ({
      bank,
      rows: list,
      subtotal: list.reduce((sum, row) => sum + row.amount, 0),
    }));
  }, [visibleRows]);

  const total = useMemo(() => visibleRows.reduce((sum, row) => sum + row.amount, 0), [visibleRows]);
  const missingBankCount = visibleRows.filter((row) => !row.hasBank).length;
  const draftCount = visibleRows.filter((row) => row.status === 'DRAFT').length;

  const firstRecord: any = payrollData?.data?.[0];
  const bounds = periodBounds(payPeriod);
  const periodStart = firstRecord?.periodStart ? String(firstRecord.periodStart).slice(0, 10) : bounds.start;
  const periodEnd = firstRecord?.periodEnd ? String(firstRecord.periodEnd).slice(0, 10) : bounds.end;

  const bankLabel = groups.length === 1 ? groups[0].bank : 'All banks';
  const hasContent = visibleRows.length > 0;

  const buildStandaloneHtml = () => {
    const sheet = document.getElementById('bank-salary-sheet');
    if (!sheet) return '';
    return (
      `<!doctype html><html><head><meta charset="utf-8" /><title>Bank Salary Statement ${payPeriod}</title>` +
      `<style>${SHEET_CSS}` +
      `@page{size:A4 portrait;margin:10mm;}` +
      `@media print{body{margin:0;}.bs-sheet{padding:0;}}` +
      `</style></head><body>${sheet.outerHTML}</body></html>`
    );
  };

  const handlePrint = () => {
    const html = buildStandaloneHtml();
    if (!html) return;

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print the bank salary sheet');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onafterprint = () => printWindow.close();
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 350);
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await hrService.downloadBankSalarySheetPdf(payPeriod, status || undefined);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Bank-Salary-Statement-${payPeriod}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success('Bank salary statement downloaded');
    } catch {
      toast.error('Failed to download the bank salary statement');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadCsv = () => {
    const header = [
      '#',
      'Employee Code',
      'Employee Name',
      'Designation',
      'Bank Name',
      'Account Number',
      'Net Salary',
      'Pay Period',
      'Status',
    ];
    const body = visibleRows.map((row, index) => [
      index + 1,
      row.code,
      row.name,
      row.designation,
      row.bank,
      row.account,
      row.amount.toFixed(2),
      payPeriod,
      row.status,
    ]);
    const csv = toCsv([header, ...body, ['', '', '', '', '', 'Total', total.toFixed(2), '', '']]);
    downloadTextFile(`Bank-Salary-Sheet-${payPeriod}.csv`, `\ufeff${csv}`);
    toast.success('Bank salary sheet downloaded');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Bank Salary Sheet"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <style>{SHEET_CSS}</style>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Landmark size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">Bank Salary Sheet</h2>
              <p className="text-sm text-text-muted">
                Print or export a month-wise salary list with bank account details for disbursement.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Pay Period</label>
              <MonthPicker value={payPeriod} onChange={setPayPeriod} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All statuses</option>
                {PAYROLL_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option.charAt(0) + option.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(missingBankCount > 0 || draftCount > 0) && (
            <div className="mt-3 space-y-2">
              {missingBankCount > 0 && (
                <div className="flex items-start gap-2 p-2.5 bg-warning/10 border border-warning/20 rounded-lg text-xs text-text">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
                  <span>
                    {missingBankCount} employee{missingBankCount === 1 ? '' : 's'} have no bank name/account on record
                    and appear under “No bank details”. Update them before sending this sheet to the bank.
                  </span>
                </div>
              )}
              {draftCount > 0 && (
                <div className="flex items-start gap-2 p-2.5 bg-warning/10 border border-warning/20 rounded-lg text-xs text-text">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
                  <span>
                    {draftCount} draft payroll record{draftCount === 1 ? '' : 's'} are included. Finalize them before
                    disbursement.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto bg-background p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load payroll records for this period.</span>
            </div>
          ) : (
            <div className="mx-auto max-w-[210mm] shadow-sm border border-border rounded-lg overflow-hidden">
              <div id="bank-salary-sheet" className="bs-sheet">
                <div className="bs-head">
                  {hospitalLogo && <img className="bs-logo" src={hospitalLogo} alt="" />}
                  <div>
                    {hospitalNameBn && <div className="bs-name-bn">{hospitalNameBn}</div>}
                    <div className="bs-name-en">{hospitalNameEn}</div>
                    {address && <div className="bs-addr">{address}</div>}
                    {(hotline || website || email) && (
                      <div className="bs-contact">
                        {[
                          hotline ? `Hotline: ${hotline}` : '',
                          website ? `Web: ${website}` : '',
                          email ? `Email: ${email}` : '',
                        ]
                          .filter(Boolean)
                          .join('  |  ')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bs-title">Bank Salary Statement</div>
                <div className="bs-subtitle">
                  {periodLabel(payPeriod)}
                  {periodStart && periodEnd
                    ? `  ·  ${formatDateLabel(periodStart)} – ${formatDateLabel(periodEnd)}`
                    : ''}
                </div>
                <div className="bs-meta">
                  <span>Bank: {bankLabel}</span>
                  <span>Employees: {visibleRows.length}</span>
                  <span>Total net pay: {money(total)}</span>
                </div>

                {!hasContent && (
                  <div className="bs-empty">
                    No payroll records for {periodLabel(payPeriod)}
                    {status ? ` with status ${status}` : ''}. Generate or adjust the payroll first.
                  </div>
                )}

                {groups.map((group) => (
                  <div key={group.bank} className="bs-section">
                    <div className="bs-section-title">
                      <span>{group.bank}</span>
                      <span className="bs-section-sub">
                        {group.rows.length} employee{group.rows.length === 1 ? '' : 's'} · {money(group.subtotal)}
                      </span>
                    </div>
                    <table className="bs-table">
                      <thead>
                        <tr>
                          <th className="bs-serial">#</th>
                          <th>Employee Code</th>
                          <th>Employee Name</th>
                          <th>Designation</th>
                          <th>Bank Name</th>
                          <th>Account Number</th>
                          <th>Net Salary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row, index) => (
                          <tr key={row.id}>
                            <td className="bs-serial">{index + 1}</td>
                            <td className="bs-code">{row.code}</td>
                            <td className="bs-name">{row.name}</td>
                            <td>{row.designation}</td>
                            <td>{row.bank}</td>
                            <td>{row.account}</td>
                            <td className="bs-num">{money(row.amount)}</td>
                          </tr>
                        ))}
                        <tr className="bs-subtotal">
                          <td colSpan={6}>Subtotal — {group.bank}</td>
                          <td className="bs-num">{money(group.subtotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}

                {hasContent && (
                  <div className="bs-grand">
                    <div className="box">
                      Grand Total: {money(total)} ({visibleRows.length} employee
                      {visibleRows.length === 1 ? '' : 's'})
                    </div>
                  </div>
                )}

                {hasContent && (
                  <div className="bs-signatures">
                    <div className="bs-sig-block">
                      <div className="bs-sig-graphic"></div>
                      <div className="bs-sig-rule">Prepared By</div>
                    </div>
                    <div className="bs-sig-block">
                      <div className="bs-sig-graphic"></div>
                      <div className="bs-sig-rule">Checked By</div>
                    </div>
                    <div className="bs-sig-block">
                      <div className="bs-sig-graphic">
                        {showSignature && sealImage && (
                          <img className="bs-sig-seal" src={sealImage} alt="Hospital seal" />
                        )}
                        {showSignature && signatureImage && (
                          <img className="bs-sig-img" src={signatureImage} alt="Authorized signature" />
                        )}
                      </div>
                      <div className="bs-sig-rule">Authorized Signature</div>
                      {showSignature && signatoryName && (
                        <div className="bs-sig-name">{signatoryName}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="bs-footer">
                  <span>{hospitalNameEn}</span>
                  <span>Bank salary statement — generated from FastAid OS.</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border flex-wrap">
          <span className="text-xs text-text-muted">
            {isLoading
              ? 'Loading payroll…'
              : `${visibleRows.length} employee${visibleRows.length === 1 ? '' : 's'} · ${money(total)} total`}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleDownloadCsv}
              disabled={isLoading || !hasContent}
              className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium disabled:opacity-50"
            >
              <FileSpreadsheet size={16} />
              CSV
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={isLoading || !hasContent || downloadingPdf}
              className="inline-flex items-center gap-2 px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium disabled:opacity-50"
            >
              {downloadingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {downloadingPdf ? 'Preparing…' : 'PDF'}
            </button>
            <button
              onClick={handlePrint}
              disabled={isLoading || !hasContent}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
